import { useMemo } from 'react';
import { Helmet } from 'react-helmet';

import { ButtonLink } from '../../components/Button';
import ErrorMessage from '../../components/ErrorMessage';
import Loading from '../../components/Loading';
import NewReservationForm, {
  NewReservationFormValues,
} from '../../components/Requests/NewReservationForm';
import { SectionHeader } from '../../components/SectionHeader';
import {
  NewReservationRequestClientTeamMemberInput,
  useRequestNewReservationMutation,
} from '../../generated/graphql';
import { useStagedFileUpload } from '../../hooks/useStagedFileUpload';
import { useUserRole } from '../../hooks/useUserRole';
import { useAppSelector } from '../../store/store';
import { popToast } from '../../utilities/popToast';

export default function NewReservation() {
  const { isOnlyTraveler } = useUserRole();
  const userProfile = useAppSelector((state) => state.user.profile);
  const {
    stagedFiles,
    isUploading: isUploadingFile,
    stageFile,
    clearFiles,
  } = useStagedFileUpload();

  // Memoize initial values to avoid recalculation on re-renders
  const initialValues = useMemo(() => {
    if (!isOnlyTraveler || !userProfile?.clientTeamMemberId) {
      return undefined;
    }
    return {
      guests: [
        {
          id: String(userProfile.clientTeamMemberId),
          firstName: userProfile.firstName || '',
          lastName: userProfile.lastName || '',
        },
      ],
    };
  }, [
    isOnlyTraveler,
    userProfile?.clientTeamMemberId,
    userProfile?.firstName,
    userProfile?.lastName,
  ]);

  const [requestNewReservation, { loading, error }] =
    useRequestNewReservationMutation({
      onCompleted: () => {
        popToast(`Request successfully submitted!`);
        clearFiles();
      },
      onError: (error) => {
        popToast(`Error submitting request: ${error.message}`, {
          type: 'error',
        });
      },
    });

  const onSubmit = (values: NewReservationFormValues) => {
    if (isOnlyTraveler && userProfile?.clientTeamMemberId) {
      const guestList = values.guests ?? [];
      const hasOtherGuests = guestList.some(
        (guest) =>
          guest.id &&
          String(guest.id) !== String(userProfile.clientTeamMemberId),
      );
      const hasNewGuests = guestList.some((guest) => !guest.id);

      if (hasOtherGuests || hasNewGuests || guestList.length > 1) {
        popToast('Travelers can only create reservations for themselves', {
          type: 'error',
        });
        return;
      }
    }

    // Group guests into existing and new arrays based on id definition
    const guests = values.guests?.reduce(
      (
        acc: {
          existingGuests: NewReservationRequestClientTeamMemberInput[];
          newGuests: NewReservationRequestClientTeamMemberInput[];
        },
        guest,
      ) => {
        // Remove fields that are not part of the GraphQL schema
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { semiUniqueIdentifier, unknownGuest, ...guestForSubmission } =
          guest as NewReservationRequestClientTeamMemberInput & {
            semiUniqueIdentifier?: string;
            unknownGuest?: boolean;
          };

        if (guestForSubmission.id) {
          acc.existingGuests.push(guestForSubmission);
        } else {
          acc.newGuests.push(guestForSubmission);
        }
        return acc;
      },
      { existingGuests: [], newGuests: [] },
    );

    // Extract the data - guests may be empty if files are attached instead
    const { existingGuests = [], newGuests = [] } = guests ?? {};
    const {
      preferredHotelAddress,
      preferredHotelEmail,
      preferredHotelName,
      preferredHotelPhone,
      controlNumber,
      requiredFields,
    } = values;

    // Map staged files to attachment input format
    const attachments = stagedFiles.map((file) => ({
      tempFilePath: file.tempFilePath,
      originalFilename: file.originalFilename,
      fileType: file.fileType,
      displayFilename: file.displayFilename,
    }));

    requestNewReservation({
      variables: {
        input: {
          controlNumber,
          existingGuests,
          newGuests,
          preferredHotelAddress,
          preferredHotelEmail,
          preferredHotelName,
          preferredHotelPhone,
          requiredFields,
          attachments: attachments.length > 0 ? attachments : undefined,
        },
      },
    });
  };

  return (
    <div className="flex items-start justify-center p-5 sm:p-10">
      <Helmet>
        <title>New Reservation</title>
      </Helmet>

      <div className="flex-1">
        {error && (
          <div className="mb-5">
            <ErrorMessage errorMessage={error.message} />
          </div>
        )}

        <>
          <SectionHeader className="font-medium" headline="New Reservation">
            <ButtonLink to="/roster" text="Return to Live Roster" />
          </SectionHeader>
          {loading ? (
            <Loading />
          ) : (
            <NewReservationForm
              onSubmit={onSubmit}
              initialValues={initialValues}
              isTraveler={isOnlyTraveler}
              stagedFiles={stagedFiles}
              onStageFile={stageFile}
              isUploadingFile={isUploadingFile}
            />
          )}
        </>
      </div>
    </div>
  );
}
